import type { CampaignCreationCapabilities, SponsoredBrandStore } from "@adpilot/shared";
import { SponsoredBrandsCreationSubmitSchema } from "@adpilot/shared";
import { z } from "zod";
import {
  type AdFormat,
  type CategoryTargetRow,
  type KeywordRow,
  type LandingPage,
  type NegativeKeywordRow,
  PLACEMENT_FIELDS,
  type PlacementField,
  type SponsoredBrandDetailsForm,
  type TargetingType,
} from "./sponsored-brands-model";

export interface SponsoredBrandsPayloadInput {
  requestId: string;
  details: SponsoredBrandDetailsForm;
  adFormat: AdFormat;
  landingPage: LandingPage;
  storeEntityId: string | null;
  storePageUrl: string | null;
  advertisedAsins: string[];
  creativeAsins: string[];
  selectedStore: SponsoredBrandStore | null;
  spotlightPageIds: string[];
  spotlightPageAsins: Record<string, string>;
  targetingType: TargetingType;
  keywordRows: KeywordRow[];
  negativeKeywords: NegativeKeywordRow[];
  productTargetAsins: string[];
  categoryTargets: CategoryTargetRow[];
  productTargetBid: string;
  negativeProductAsins: string[];
  automatedBidding: boolean;
  placementAdjustmentsEnabled: boolean;
  placementValues: Record<PlacementField, string>;
}

export function isMoneyWithinCapabilities(
  value: number,
  range: { min: number; max: number; decimals: number },
): boolean {
  if (value < range.min || value > range.max) return false;
  const scale = 10 ** range.decimals;
  return Math.abs(value * scale - Math.round(value * scale)) <= 1e-8;
}

// The shared submit schema is marketplace-agnostic; name length, budget and bid limits come from
// the selected marketplace's capabilities, so they are layered on here.
function createCapabilityAwareSubmitSchema(capabilities: CampaignCreationCapabilities) {
  return SponsoredBrandsCreationSubmitSchema.superRefine((payload, ctx) => {
    if (payload.campaign.name.length > capabilities.campaignName.maxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Campaign name exceeds this marketplace's limit",
        path: ["campaign", "name"],
      });
    }
    if (!isMoneyWithinCapabilities(payload.campaign.dailyBudget, capabilities.dailyBudget)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Daily budget is outside this marketplace's limits",
        path: ["campaign", "dailyBudget"],
      });
    }
    payload.targeting.targets.forEach((target, index) => {
      if (isMoneyWithinCapabilities(target.bid, capabilities.bid)) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Bid is outside this marketplace's limits",
        path: ["targeting", "targets", index, "bid"],
      });
    });
  });
}

/** Maps the form state to the SB submit contract and validates it against marketplace limits. */
export function buildSponsoredBrandsPayload(
  input: SponsoredBrandsPayloadInput,
  capabilities: CampaignCreationCapabilities,
) {
  const {
    details,
    adFormat,
    landingPage,
    storeEntityId,
    storePageUrl,
    advertisedAsins,
    creativeAsins,
  } = input;
  const commonCreative = {
    brandName: details.brandName,
    headline: details.headline,
    brandLogoAssetId: details.logoAssetId ?? "",
    brandLogoCrop: { top: 0, left: 0, width: 400, height: 400 },
  };
  const ad =
    adFormat === "productCollection"
      ? {
          format: "product_collection" as const,
          name: details.adName,
          products: creativeAsins,
          landingPage:
            landingPage === "amazonStore"
              ? {
                  type: "store" as const,
                  storeEntityId: storeEntityId ?? "",
                  url: storePageUrl ?? "",
                }
              : {
                  type: "product_list" as const,
                  asins: advertisedAsins,
                },
          creative: {
            ...commonCreative,
            customImageAssetId: details.customImageAssetId,
            customImageCrop: details.customImageAssetId
              ? { top: 0, left: 0, width: 1_200, height: 628 }
              : null,
          },
        }
      : adFormat === "video"
        ? {
            format: "video" as const,
            name: details.adName,
            products: advertisedAsins.slice(0, 1),
            landingPage:
              landingPage === "amazonStore"
                ? {
                    type: "store" as const,
                    storeEntityId: storeEntityId ?? "",
                    url: storePageUrl ?? "",
                  }
                : {
                    type: "product_detail" as const,
                    asin: advertisedAsins[0] ?? "",
                  },
            creative: {
              videoAssetId: details.videoAssetId ?? "",
              brand: landingPage === "amazonStore" ? commonCreative : null,
            },
          }
        : {
            format: "store_spotlight" as const,
            name: details.adName,
            landingPage: {
              type: "store" as const,
              storeEntityId: storeEntityId ?? "",
              url: storePageUrl ?? "",
            },
            creative: {
              ...commonCreative,
              subpages: (input.selectedStore?.pages ?? [])
                .filter((page) => input.spotlightPageIds.includes(page.id))
                .map((page) => ({
                  pageId: page.id,
                  pageTitle: page.name,
                  asin: input.spotlightPageAsins[page.id] ?? "",
                  url: page.url,
                })),
            },
          };
  const targeting =
    input.targetingType === "keyword"
      ? {
          type: "keyword" as const,
          targets: input.keywordRows.map((row) => ({
            text: row.keyword,
            matchType: ({ B: "BROAD", P: "PHRASE", E: "EXACT" } as const)[row.matchType],
            bid: Number(row.bid),
          })),
          negatives: input.negativeKeywords.map((row) => ({
            text: row.keyword,
            matchType:
              row.matchType === "negativeExact"
                ? ("NEGATIVE_EXACT" as const)
                : ("NEGATIVE_PHRASE" as const),
          })),
        }
      : {
          type: "product" as const,
          targets: [
            ...input.productTargetAsins.map((asin) => ({
              expressionType: "ASIN_SAME_AS" as const,
              value: asin,
              bid: Number(input.productTargetBid),
            })),
            ...input.categoryTargets.map((category) => ({
              expressionType: "ASIN_CATEGORY_SAME_AS" as const,
              value: category.id,
              bid: Number(input.productTargetBid),
            })),
          ],
          negatives: input.negativeProductAsins.map((asin) => ({
            expressionType: "ASIN_SAME_AS" as const,
            value: asin,
          })),
        };
  const placementAdjustments =
    !input.automatedBidding && input.placementAdjustmentsEnabled
      ? PLACEMENT_FIELDS.map(({ key, placement }) => ({
          placement,
          percentage:
            input.placementValues[key] === "" ? Number.NaN : Number(input.placementValues[key]),
        }))
      : [];
  return createCapabilityAwareSubmitSchema(capabilities).safeParse({
    adProduct: "sb",
    requestId: input.requestId,
    campaign: {
      state: "PAUSED",
      name: details.campaignName,
      // SB v4 has not confirmed a portfolioId field yet, so the portfolio stays unset.
      portfolioId: null,
      brandEntityId: details.brandEntityId ?? "",
      startDate: details.startDate,
      endDate: details.endDate || null,
      dailyBudget: Number(details.budget),
      bidding: {
        automated: input.automatedBidding,
        placementAdjustments,
        newToBrandAdjustmentPercentage: null,
      },
    },
    adGroup: { name: details.adGroupName },
    ad,
    targeting,
  });
}

export type SponsoredBrandsPayloadResult = ReturnType<typeof buildSponsoredBrandsPayload>;
